<template>
  <base-block-tmp
    class="recipe-collection-card"
    :size="size"
    type="recipeCollection"
    :layout-type="layoutType"
    :small-card-type="smallCardType"
    :url="url"
    :label-text="`${prefix} ${title}`"
    @simulateClick="simulateClickOnLink"
  >
    <template #media>
      <image-grid
        v-edit="'RecipeId'"
        highlight-first-image
        :image-urls="imageUrls"
        :scroll-container-id="scrollContainerId"
        :dimensions="dimensions"
        :is-full-size="isSize.full"
      />
      <epi-property
        id="RecipeIds"
        :icon-only="true"
        class="button--recipeid-edit"
      />
    </template>
    <template #content>
      <div
        class="recipe-collection-card__content__prefix"
        v-text="prefix"
      />
      <h3
        v-edit="'Title'"
        aria-label="Receptsamling"
        class="recipe-collection-card__content__title"
      >
        <a
          v-if="($root.isSmallViewport && url) || (url && !buttonLabel)"
          ref="linkref"
          :href="url"
          :aria-label="title"
          class="no-link-look"
          @click="trackClick()"
        >{{ title }}</a>
        <span v-else>{{ title }}</span>
      </h3>
      <div
        v-if="($root.isMediumViewport && isHighlight) || ($root.isLargeViewport && isSize.full) || isEditable"
        v-edit="'Preamble'"
        class="recipe-collection-card__content__preamble"
        v-text="preamble"
      />
      <a
        v-if="(buttonLabel && (($root.isMediumViewport || $root.isLargeViewport) && isSize.full)) || isEditable"
        :ref="(url && buttonLabel) ? 'linkref' : 'notUsed'"
        v-edit="'ButtonLabel'"
        :href="url"
        class="button--secondary recipe-collection-card__content__button"
        :class="($root.isMediumViewport || $root.isSmallViewport) ? 'button--40' : 'button--48'"
        @click.stop="trackClick()"
        v-text="buttonLabel"
      />
      <epi-property
        v-if="buttonLabel"
        id="recipeUrl"
        text="Länk till receptsamling"
      />
    </template>
  </base-block-tmp>
</template>

<script>
import BaseBlockTmp, { layoutTypes } from './BaseBlockComponent'
import ImageGrid from './ImageGrid.vue'
import dataLayer from '@/analytics/dataLayer'

export default {
  name: 'RecipeCollectionCard',
  components: {
    BaseBlockTmp,
    ImageGrid
  },
  props: {
    blockSize: {
      type: String,
      default: 'quarter',
      validator (value) {
        return ['full', 'half', 'third', 'quarter'].includes(value)
      }
    },
    dimensions: {
      type: Object,
      default: () => ({
        small: {
          width: 220
        },
        medium: {
          width: 420
        },
        large: {
          width: 600
        }
      })
    },
    url: {
      type: String,
      default: ''
    },
    imageUrls: {
      type: Array,
      default: () => []
    },
    prefix: {
      type: String,
      default: 'Receptsamling'
    },
    title: {
      type: String,
      default: ''
    },
    preamble: {
      type: String,
      default: ''
    },
    buttonLabel: {
      type: String,
      default: null
    },
    smallCardType: {
      type: Boolean,
      default: false
    },
    scrollContainerId: {
      type: String,
      default: null
    },
    tracking: {
      type: Object,
      default: function () {
        return {}
      }
    },
  },
  computed: {
    size () {
      return this.isEditable ? 'full' : this.blockSize
    },
    primaryImage () {
      return this.imageUrls.length ? this.imageUrls[0] : ''
    },
    secondaryImages () {
      let secondary = this.imageUrls.length > 1 ? this.imageUrls.slice(1) : []
      for (let i = secondary.length; i < 3; i++) {
        secondary.push('')
      }

      return secondary
    },
    isSize () {
      return {
        [this.size]: true
      }
    },
    isHighlight () {
      return ['full', 'half'].includes(this.size)
    },
    layoutType () {
      switch(this.$root.currentSize) {
        case 'sm':
        case 'xs':
        case 'xxs':
          return this.isHighlight ? layoutTypes.mediaTop : layoutTypes.mediaLeft
        case 'md':
          return this.isHighlight ? layoutTypes.mediaLeft : layoutTypes.mediaTop
        case 'lg':
        case 'xl':
          return this.isSize.full ? layoutTypes.mediaLeft : layoutTypes.mediaTop
      }

      return layoutTypes.mediaTop
    }
  },
  methods: {
    simulateClickOnLink () {
      this.$refs.linkref?.click()
    },
    trackClick () {
      dataLayer.add({
        event: 'recipeCollectionSelect',
        linkName: this.title,
        linkUrl: !/^https?:\/\//i.test(this.url) ? window.location.origin + this.url : this.url,
        ...this.tracking
      })
    }
  }
}
</script>

<style lang="scss">
.recipe-collection-card {
  $this: &;

  .button--recipeid-edit.button--editbutton {
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
  }

  .base-block-tmp--media-left &__media {
    margin-right: -0.5rem;
  }

  .base-block-tmp__content-slot {
    @at-root {
      .base-block-tmp--media-top#{&} {
        margin-top: -0.5rem;
      }
    }

    &__padding {
      margin: auto;

      @include media-breakpoint-up(md) {
        //padding: 1.5rem 13rem;
      }

      #{$this}__content__prefix {
        @include font(hand, 1, 'normal');

        color: $color-red;
      }

      #{$this}__content__title {
        $title: &;

        margin: 0;
        text-transform: none;
      }

      #{$this}__content__preamble {
        @include font(text, 3, 'light');

        margin: 1rem 0 0;

        @include media-breakpoint-up(lg) {
          @include font(text, 4, 'light');

          margin: 1.5rem 0 0;
        }
      }

      #{$this}__content__button {
        margin-top: 2rem;

        @include media-breakpoint-up(lg) {
          margin-top: 2.5rem;
        }
      }
    }
  }

  $contentPadding: '.base-block-tmp__content-slot .base-block-tmp__content-slot__padding';

  // sm & md padding
  &.base-block-tmp.base-block-tmp--full #{$contentPadding} {
    padding: 1.5rem 1.5rem 2rem;
  }

  @include media-breakpoint-up(md) {
    &.base-block-tmp.base-block-tmp--full #{$contentPadding} {
      padding: 3.5rem 3rem;
    }
    &.base-block-tmp:not(.base-block-tmp--full) #{$contentPadding} {
      padding: 1.5rem 1.5rem 2rem;
    }
  }

  // lg padding
  @include media-breakpoint-up(lg) {
    &.base-block-tmp:not(.base-block-tmp--full) #{$contentPadding} {
      padding: 2.5rem 1rem;
    }
  }

  // xl padding
  @include media-breakpoint-up(xl) {
    &.base-block-tmp.base-block-tmp--half #{$contentPadding} {
      padding: 2.5rem;
    }
  }

  body:not([ie11]) &.base-block-tmp--media-top .base-block-tmp__media-lg {
    @include media-breakpoint-up(md) {
      flex: 0%; // Fix for safari
    }
  }
}
</style>
